import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  FetchCustomersForTransactionAccountQuery,
  FetchCustomersForTransactionAccountQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { CustomerEmpty } from './components/customer-empty';
import { CustomerSelect } from './components/customer-select';
import { CustomerSelected } from './components/customer-selected';
import { CartCustomerSearchItem, CartCustomerSearchItemMap } from './types';
import { fetchCustomers } from '../../../queries/fetch-customers';
import { AddCustomerModal } from './components/add-customer-modal';

enum CartCustomerViewEnum {
  EMPTY = 'empty',
  SELECT = 'select',
  SELECTED = 'selected',
}

interface CustomerWrapperProps {
  onCustomerChange: (customer: CartCustomerSearchItem | null) => void;
}

const getCustomerList = (data: FetchCustomersForTransactionAccountQuery | undefined) => {
  const shipToParties = data?.transactionAccount?.availableShipToParties?.nodes ?? [];
  const companyNameObj: CartCustomerSearchItemMap = shipToParties.reduce((companyNameAcc, node) => {
    if (!node || !node.name || !node.id) {
      return;
    }
    companyNameAcc[node.name] = companyNameAcc[node.name] || ({ name: node.name, ids: [] } as CartCustomerSearchItem);
    companyNameAcc[node.name].ids.push(node.id);
    return companyNameAcc;
  }, {} as any);

  return Object.values(companyNameObj);
};

export const CustomerWrapper = ({ onCustomerChange }: CustomerWrapperProps) => {
  const [currentView, setCurrentView] = useState<CartCustomerViewEnum>(CartCustomerViewEnum.EMPTY);
  const [selectedCustomerName, setSelectedCustomerName] = useState<CartCustomerSearchItem['name'] | undefined>(
    undefined
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>();
  const [showAddCustomerModal, setShowAddCustomerModal] = useState<boolean>(false);
  const [searchList, setSearchList] = useState<CartCustomerSearchItem[]>();
  const { txaId } = useParams<{ txaId: string }>();

  const { data, loading, refetch } = useQuery<
    FetchCustomersForTransactionAccountQuery,
    FetchCustomersForTransactionAccountQueryVariables
  >(fetchCustomers, {
    variables: {
      id: txaId,
    },
    skip: !txaId,
  });

  const allCustomers = useMemo(() => getCustomerList(data), [data]);
  const selectedCustomer = useMemo(
    () =>
      allCustomers?.find(
        (option) =>
          // case when user selects customer name
          // case when user adds a new customer id
          (selectedCustomerName && option.name === selectedCustomerName) ||
          (selectedCustomerId && option.ids.includes(selectedCustomerId))
      ),
    [allCustomers, selectedCustomerName, selectedCustomerId]
  );

  useEffect(() => {
    if (!selectedCustomer) {
      return;
    }
    setSelectedCustomerName(selectedCustomer.name);
    handleCustomerSelection(selectedCustomer);
  }, [selectedCustomerName, selectedCustomer]);

  useEffect(() => {
    onCustomerRemove();
  }, [txaId]);

  const onAddCompany = () => {
    setCurrentView(CartCustomerViewEnum.SELECT);
  };

  const handleCustomerSelection = (customer: CartCustomerSearchItem) => {
    setCurrentView(CartCustomerViewEnum.SELECTED);
    onCustomerChange(customer);
  };

  const onCustomerSelect = (selectedCustomer: CartCustomerSearchItem) => {
    if (!selectedCustomer) {
      return;
    }
    setSelectedCustomerName(selectedCustomer?.name);
  };

  const onCustomerRemove = () => {
    setCurrentView(CartCustomerViewEnum.EMPTY);
    onCustomerChange(null);
    setSelectedCustomerName(undefined);
    setSelectedCustomerId(undefined);
    setSearchList([]);
  };

  const onSearch = (query: string) => {
    const searchQuery = query.toLowerCase();
    setSearchList(allCustomers?.filter(({ name }) => name.toLowerCase().includes(searchQuery)));
  };

  const onAddCustomerSuccess = (responsePayload?: { id?: string }) => {
    setCurrentView(CartCustomerViewEnum.EMPTY);
    setShowAddCustomerModal(false);
    setSelectedCustomerName(undefined);
    setSelectedCustomerId(responsePayload?.id);
    return refetch();
  };

  const renderComponents = () => {
    switch (currentView) {
      case CartCustomerViewEnum.EMPTY:
        return <CustomerEmpty onAddClick={onAddCompany} isLoading={loading} />;
      case CartCustomerViewEnum.SELECT:
        return (
          <CustomerSelect
            onSelect={onCustomerSelect}
            onSearch={onSearch}
            searchList={searchList}
            onAddCustomerClick={setShowAddCustomerModal.bind(null, true)}
          />
        );
      case CartCustomerViewEnum.SELECTED:
        return selectedCustomer ? (
          <CustomerSelected onCancelClick={onCustomerRemove} customer={selectedCustomer} />
        ) : null;
    }
  };
  return (
    <div className="pa-16">
      {renderComponents()}
      <AddCustomerModal
        onSuccess={onAddCustomerSuccess}
        isOpen={showAddCustomerModal}
        transactionAccountId={txaId}
        onClose={setShowAddCustomerModal.bind(null, false)}
      />
    </div>
  );
};
